import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import { RiseAltLogo } from "@/assets/svg/RiseAltLogo";
import { useReactiveVar, useQuery } from "@apollo/client";
import { EmptyStateComponent } from "@/components/EmptyStateComponent";
import {
  OrganizationPartnershipLevelEnum,
  PartnerFeatureEnum,
} from "@/__generated__/types";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { AnalyticsEvent, useAnalytics } from "@/hooks/useAnalytics";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useOrg } from "@/hooks/useOrg";
import { CircleX, FileDown, Share } from "lucide-react";
import { FunnelStudents } from "./FunnelStudents";
import { format } from "date-fns";
import {
  FunnelsChart,
  Funnel,
  SelectedFunnelBar,
} from "@/components/FunnelsChart";
import { FilterName } from "@/components/FilterSidebar/types";
import {
  filterStateVar,
  initialFilterStateVar,
} from "@/reactiveVars/organization";
import { useMultiSidebar } from "@/components/ui/MultiSidebar";
import { FUNNEL_CHART_DATA } from "@/api/queries/funnelChartData";
import { FunnelChartWrapper } from "@/components/FunnelChartWrapper";
import { selectedFunnelBarsEqual, processQueryResponse } from "./utils";
import { useFilterSidebarQuery } from "@/hooks/useFilterSidebarQuery";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";
import { getGroupNames } from "@/util/funnelGroups";
import { useBuildUrlState, useUrlState } from "@/hooks/useUrlSerializer";
import { useRoute } from "@/routes/getRoute";
import { useShareLink } from "@/hooks/useShareLink";
import { PageTitle } from "@/components/PageTitle";
import { DateInputBar } from "./DateInputBar";
import { FilterState } from "@/components/FilterSidebar/FilterState";

const ERROR_STATE_HEIGHT = "292px";

export const ZeemeeFunnel = () => {
  const { t } = useTranslation();
  const { org } = useOrg();
  const { shareLink } = useShareLink();
  const getRoute = useRoute();
  const { trackEvent } = useAnalytics();

  // data that can be retrieved from url
  const {
    date,
    filterState: initialFilterState,
    compareDate,
    compareFilterState: initialCompareFilterState,
  } = useUrlState("FUNNEL_CHART") || {};

  const { hasOrgFeature } = useOrgPermissions();
  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  const [selectedDate, setSelectedDate] = useState(() => date || new Date());
  const [selectedCompareDate, setSelectedCompareDate] = useState<
    Date | undefined
  >(compareDate);

  const [selectedFunnelBar, setSelectedFunnelBar] =
    useState<SelectedFunnelBar | null>(null);

  // filter state of main / left funnel
  const [leftFilterState, setLeftFilterState] = useState(
    initialFilterState || new FilterState({}),
  );
  // filter state of compare / right funnel
  const [compareFilterState, setCompareFilterState] = useState(
    initialCompareFilterState || new FilterState({}),
  );

  const [activeEditFunnel, setActiveEditFunnel] = useState<
    "left" | "right" | null
  >(null);

  const { open: sidebarOpen, setOpen } = useMultiSidebar("filter");
  const filterState = useReactiveVar(filterStateVar);
  const prevFilterState = useRef(filterState);

  // close filter sidebar when adding compare date
  const setCompareDate = useCallback(
    (date: Date | undefined) => {
      setSelectedCompareDate(date);
      setActiveEditFunnel(null);
      setOpen(false);
      if (date === undefined) {
        initialFilterStateVar(leftFilterState);
        filterStateVar(leftFilterState);
      }
    },
    [leftFilterState, setOpen],
  );

  useEffect(() => {
    if (!sidebarOpen) {
      setActiveEditFunnel(null);
      // else, if sidebar is OPEN we force focus left funnel if neither is focused
    } else if (activeEditFunnel === null) {
      initialFilterStateVar(leftFilterState);
      filterStateVar(leftFilterState);
      setActiveEditFunnel("left");
    }
  }, [activeEditFunnel, sidebarOpen, leftFilterState]);

  useEffect(() => {
    if (
      selectedCompareDate !== undefined &&
      filterState !== prevFilterState.current
    ) {
      setOpen(false);
    }
  }, [filterState, selectedCompareDate, setOpen]);

  // filter sidebar state was just updated, assign to active funnel
  useEffect(() => {
    if (activeEditFunnel === "left") {
      setLeftFilterState(filterState || new FilterState({}));
    } else if (activeEditFunnel === "right") {
      setCompareFilterState(filterState || new FilterState({}));
    }
    prevFilterState.current = filterState;
  }, [filterState, activeEditFunnel]);

  // get url query objects for share link construction
  const shareUrlQuery = useBuildUrlState("FUNNEL_CHART")({
    date: selectedDate,
    compareDate: selectedCompareDate,
    filterState: leftFilterState,
    compareFilterState: compareFilterState,
  });

  // left funnel data query
  const { data, loading, error } = useFilterSidebarQuery(
    useQuery(FUNNEL_CHART_DATA, {
      variables: {
        orgId: org?.id || "",
        date: format(selectedDate, "yyyy-MM-dd"),
        filters: leftFilterState?.getQueryFilter() || {},
      },
      skip: !org?.id,
    }),
    "funnelChart",
  );

  // right / compare funnel data query
  const {
    data: compareData,
    loading: compareLoading,
    error: compareError,
  } = useFilterSidebarQuery(
    useQuery(FUNNEL_CHART_DATA, {
      variables: {
        orgId: org?.id || "",
        date: selectedCompareDate && format(selectedCompareDate, "yyyy-MM-dd"),
        filters: compareFilterState?.getQueryFilter() || {},
      },
      skip: !org?.id || !selectedCompareDate,
    }),
    "funnelChart",
  );

  const { hasMinOrgPartnershipLevel } = useOrgPermissions();
  const canUseFilters =
    hasMinOrgPartnershipLevel(OrganizationPartnershipLevelEnum.Pro) ?? false;

  const handleBarClick = useCallback(
    (selectedBar: SelectedFunnelBar) => {
      if (selectedFunnelBarsEqual(selectedBar, selectedFunnelBar)) {
        setSelectedFunnelBar(null);
      } else {
        setSelectedFunnelBar(selectedBar);

        const communityLabel =
          selectedBar.communityStatus === "inCommunity"
            ? "In Community"
            : "Not In Community";

        trackEvent(AnalyticsEvent.FunnelStateClicked, {
          "Funnel State": t(`funnelGroups.${selectedBar.stage}`),
          "Community": communityLabel,
        });
      }
    },
    [selectedFunnelBar, trackEvent, t],
  );

  const chartData = useMemo(() => processQueryResponse(data), [data]);
  const compareChartData = useMemo(
    () => processQueryResponse(compareData),
    [compareData],
  );

  // TODO: will need this when we re-enable the funnel table
  // const funnelTableData = chartData ? computeFunnelTableData(chartData) : [];

  const handleClear = () => setSelectedFunnelBar(null);

  const handleExportCSV = () => {
    const inCommunityCountHeader = t(
      "zeemeeFunnel.csvHeaders.inCommunityCount",
    );
    const notInCommunityCountHeader = t(
      "zeemeeFunnel.csvHeaders.notInCommunityCount",
    );

    const funnelStates = getGroupNames("FUNNEL");
    const csvData = funnelStates.map((state) => {
      return {
        "": t(`funnelGroups.${state}`),
        [inCommunityCountHeader]: chartData?.inCommunity?.find(
          (data) => data.stage === state,
        )?.count,
        [notInCommunityCountHeader]: chartData?.notInCommunity?.find(
          (data) => data.stage === state,
        )?.count,
      };
    });

    exportChartCSV({
      filename: timestampFilename("funnel-chart"),
      headers: ["", inCommunityCountHeader, notInCommunityCountHeader],
      data: csvData,
    });

    toast.success(t("global.exportToCSV.success"));
  };

  const menuOptions = [
    {
      label: t("global.exportToCSV.menuTitle"),
      onClick: handleExportCSV,
      icon: <FileDown className="h-4 w-4" />,
    },
    {
      label: t("global.share"),
      onClick: () =>
        shareLink(
          getRoute(
            "funnel",
            {},
            {
              ...shareUrlQuery,
            },
            true,
          ),
        ),
      icon: <Share aria-hidden className="h-4 w-4" />,
    },
  ];

  const handlePillFilterToggle = (side: Funnel) => {
    const funnelSelection = activeEditFunnel === side ? null : side;
    if (funnelSelection === null) {
      setOpen(false);
    } else if (funnelSelection === "left") {
      // update sidebar state to reflect left funnel state
      initialFilterStateVar(leftFilterState);
      filterStateVar(leftFilterState);
      prevFilterState.current = leftFilterState;
      setOpen(true);
    } else if (funnelSelection === "right") {
      // update sidebar state to reflect right funnel state
      initialFilterStateVar(compareFilterState);
      filterStateVar(compareFilterState);
      prevFilterState.current = compareFilterState;
      setOpen(true);
    }
    setActiveEditFunnel(funnelSelection);
  };

  const handleRemoveFilter = (name: FilterName, side: Funnel) => {
    if (side === "left") {
      const newState = leftFilterState.removeFilter(name);
      setLeftFilterState(newState);
      initialFilterStateVar(newState); // sets state of filter sidebar form
    } else {
      const newState = compareFilterState.removeFilter(name);
      setCompareFilterState(newState);
      initialFilterStateVar(newState); // sets state of filter sidebar form
    }
  };

  return (
    <div>
      <PageTitle
        logoComponent={hasRise && <RiseAltLogo height={18} width={38} />}
        title={t("zeemeeFunnel.chart.titlePage")}
        className="border-b"
      />
      <div className="flex flex-col gap-6 p-6">
        <DateInputBar
          date={selectedDate}
          compareDate={selectedCompareDate}
          onDateChange={setSelectedDate}
          onCompareDateChange={setCompareDate}
          allowCompare={hasRise}
        />
        <div className="w-full text-foreground">
          <FunnelChartWrapper
            title={t("zeemeeFunnel.chart.title")}
            subtitle={
              selectedCompareDate
                ? t("zeemeeFunnel.chart.multiFunnelDescription")
                : t("zeemeeFunnel.chart.singleFunnelDescription")
            }
            date={selectedDate}
            compareDate={hasRise ? selectedCompareDate : undefined}
            menuOptions={menuOptions}
            showRiseIcon={!!(hasRise && selectedCompareDate)}
          >
            {error || compareError ? (
              <ErrorState />
            ) : (
              <FunnelsChart
                funnelData={chartData}
                compareFunnelData={hasRise ? compareChartData : undefined}
                focusedFunnel={selectedCompareDate ? activeEditFunnel : "left"}
                selectedBar={selectedFunnelBar}
                onBarClick={handleBarClick}
                isLoading={loading}
                isCompareLoading={compareLoading}
                filterState={leftFilterState}
                compareFilterState={compareFilterState}
                onAddFilter={handlePillFilterToggle}
                onRemoveFilter={handleRemoveFilter}
                showFilterPills={canUseFilters}
                allowNotInCommunitySelection={hasRise}
              />
            )}
          </FunnelChartWrapper>
        </div>
        {/* 
          TODO: make this collapsible
          <SingleFunnelTable
          data={funnelTableData}
          leftHeaderColor="var(--funnel-1-left)"
          rightHeaderColor="var(--funnel-1-right)"
          leftColor="var(--funnel-1-table-background)"
          rightColor="var(--funnel-1-table-light-background)"
        /> */}
        <FunnelStudents
          selectedFunnelState={selectedFunnelBar?.stage || null}
          onClearSelection={handleClear}
          filterState={
            selectedFunnelBar?.funnel === "right"
              ? compareFilterState
              : leftFilterState
          }
          communityStatus={selectedFunnelBar?.communityStatus || null}
          date={
            selectedFunnelBar?.funnel === "right" && selectedCompareDate
              ? selectedCompareDate
              : selectedDate
          }
        />
      </div>
    </div>
  );
};

const ErrorState = () => {
  const { t } = useTranslation();
  return (
    <div>
      <EmptyStateComponent
        height={ERROR_STATE_HEIGHT}
        message={t("zeemeeFunnel.chart.error")}
        icon={<CircleX className="size-12" />}
      />
    </div>
  );
};
